import { Controller, useForm } from "react-hook-form";
import { COMMENT_OBJECT_TYPE } from "common/constants";
import { useCreateCommentMutation } from "../../generated/graphql";
import { useStatusToasts } from "../../hooks/useStatusToasts";
import { track } from "../../utils/tracking";
import { CommonModal } from "../Common/CommonModal";
import { RichText } from "../Inputs/RichText";

export interface AddCommentFormProps {
  objectId: string;
  objectPartitionKey?: string;
  objectType: COMMENT_OBJECT_TYPE;
  onAdd: () => void;
  onCancel: () => void;
}

type CommentFormValues = {
  comment: string;
};

export const AddCommentForm = ({
  objectId,
  objectPartitionKey,
  objectType,
  onCancel,
  onAdd,
}: AddCommentFormProps) => {
  const { addErrorToast } = useStatusToasts();

  const { watch, handleSubmit, control } = useForm<CommentFormValues>({
    defaultValues: {
      comment: "",
    },
  });

  const [createComment, { loading }] = useCreateCommentMutation({
    onCompleted: response => {
      if (response.createComment.id) {
        track("Comment Created");
      }
      onAdd();
    },
    onError: () => {
      addErrorToast(
        `There was an error adding this comment. Please try again. If the problem persists, please email us at support@withforerunner.com`,
      );
    },
  });

  const addComment = async ({ comment }: CommentFormValues) => {
    if (loading) return;
    const cleanContent = comment.trim();

    await createComment({
      variables: {
        data: {
          content: cleanContent,
          objectId,
          objectPartitionKey,
          objectType,
        },
      },
    });
  };

  return (
    <CommonModal
      height="short"
      dataTestId="add-comment-form"
      title={"Add comment"}
      onSubmit={handleSubmit(addComment)}
      primaryButtonText="Add"
      onCancel={onCancel}
      loading={loading}
      disabled={watch("comment").trim().length === 0}
      size="small"
      content={
        <Controller
          name="comment"
          control={control}
          render={({ field, fieldState }) => (
            <RichText
              name={field.name}
              value={field.value || ""}
              onChange={field.onChange}
              error={fieldState.error?.message}
              required={true}
              label="Description"
              autoFocus
            />
          )}
        />
      }
    />
  );
};
